import { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db, PLAN_LIMITS, DEFAULT_USER_METRICS } from '../firebase/config';
import { useAuth } from './AuthContext';

const UserMetricsContext = createContext();

export const useUserMetrics = () => {
  const context = useContext(UserMetricsContext);
  if (!context) {
    throw new Error('useUserMetrics must be used within a UserMetricsProvider');
  }
  return context;
};

export const UserMetricsProvider = ({ children }) => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setMetrics(null);
      setLoading(false);
      return;
    }

    const metricsRef = doc(db, 'userMetrics', user.uid);
    
    // Subscribe to real-time updates
    const unsubscribe = onSnapshot(metricsRef, async (doc) => {
      if (!doc.exists()) {
        // Initialize metrics for new user
        await setDoc(metricsRef, DEFAULT_USER_METRICS);
        setMetrics(DEFAULT_USER_METRICS);
      } else {
        setMetrics(doc.data());
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const checkStorageLimit = async (additionalBytes = 0) => {
    if (!metrics) return false;
    const limit = PLAN_LIMITS[metrics.plan].maxStorage;
    return (metrics.storageUsed + additionalBytes) <= limit;
  };

  const checkRoomLimit = async () => {
    if (!metrics) return false;
    const limit = PLAN_LIMITS[metrics.plan].maxRooms;
    return metrics.roomCount < limit;
  };

  const checkBinLimit = async (roomId) => {
    if (!metrics) return false;
    const limit = PLAN_LIMITS[metrics.plan].maxBinsPerRoom;
    
    // Get current bin count for the room
    const roomRef = doc(db, 'rooms', roomId);
    const roomDoc = await getDoc(roomRef);
    if (!roomDoc.exists()) return false;
    
    const binCount = roomDoc.data().binCount || 0;
    return binCount < limit;
  };

  const updateMetrics = async (updates) => {
    if (!user) return;
    const metricsRef = doc(db, 'userMetrics', user.uid);
    await updateDoc(metricsRef, updates);
  };

  const value = {
    metrics,
    loading,
    checkStorageLimit,
    checkRoomLimit,
    checkBinLimit,
    updateMetrics,
    limits: metrics ? PLAN_LIMITS[metrics.plan] : null
  };

  return (
    <UserMetricsContext.Provider value={value}>
      {children}
    </UserMetricsContext.Provider>
  );
}; 